import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useOutletContext, useParams } from 'react-router-dom';
import { useAuth } from '@clerk/clerk-react';

import { createApiClient } from '../api.js';
import { useConfirm } from '../components/ConfirmDialog.jsx';
import { ContentPreview, ThemeScope } from './previews.jsx';
import {
  CheckIcon,
  EyeOffIcon,
  RefreshIcon,
  DeleteIcon,
} from '../components/icons';

/**
 * Read-only drill-down for one content item (spotlight / tooltip /
 * hotspot / modal / banner).  Mirrors TourDetail's pattern: header
 * with status + content-type badge, URL patterns, CTA details, and
 * a visual preview of how the item would render.
 */
export function ContentDetail() {
  const { content_id: contentId } = useParams();
  const { getToken } = useAuth();
  const { project } = useOutletContext() || {};
  const api = useMemo(
    () => createApiClient(getToken, { projectId: project?.project_id || null }),
    [getToken, project?.project_id],
  );

  const [item, setItem] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const c = await api(`/v1/admin/content/${encodeURIComponent(contentId)}`);
      setItem(c);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [api, contentId]);

  useEffect(() => { load(); }, [load]);

  if (loading) return <div className="state"><div className="state__title">Loading…</div></div>;
  if (error || !item) {
    return (
      <div className="state state--error">
        <div className="state__title">Couldn't load</div>
        <div>{error || `No content with id ${contentId}`}</div>
      </div>
    );
  }

  return (
    <div style={{ marginTop: 4 }}>
      <div style={{ marginBottom: 12 }}>
        <Link to="../content" className="btn btn--ghost" style={{ padding: 0 }}>← All content</Link>
      </div>

      <header className="card" style={{ display: 'flex', gap: 24, justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap' }}>
        <div>
          <div style={{ fontSize: 11, color: 'var(--text-3)', fontFamily: 'var(--font-mono)' }}>
            <code>{item.content_id}</code>
          </div>
          <h2 style={{ margin: '4px 0 6px' }}>{item.title}</h2>
          {item.body && (
            <p style={{ margin: 0, color: 'var(--text-3)', fontSize: 13.5 }}>{item.body}</p>
          )}
        </div>
        <div className="row" style={{ gap: 6, flexWrap: 'wrap' }}>
          <span className={`badge ${statusCls(item.status)}`}>{item.status}</span>
          <span className="badge badge--viewer">{item.content_type}</span>
          {item.priority !== undefined && (
            <span className="badge badge--default">priority {item.priority}</span>
          )}
          <ContentLifecycle item={item} api={api} onChanged={setItem} />
        </div>
      </header>

      <section className="card" style={{ marginTop: 14 }}>
        <h3 className="card__title">Preview</h3>
        <p className="card__body">
          Standalone render of what the SDK pops on the page. Not
          attached to anything — just the palette and layout.
        </p>
        <ThemeScope className="theme-scope--preview">
          <ContentPreview item={item} />
        </ThemeScope>
      </section>

      <section className="card" style={{ marginTop: 14 }}>
        <h3 className="card__title">Where it fires</h3>
        {(item.url_patterns || []).length === 0 ? (
          <div className="list__meta" style={{ marginTop: 8 }}>
            No URL patterns — the item won't auto-fire anywhere.
          </div>
        ) : (
          <ul className="list" style={{ marginTop: 10 }}>
            {item.url_patterns.map((p, i) => (
              <li key={i} className="list__row list__row--muted">
                <div className="list__primary">
                  <span className="list__name"><code>{p.pattern}</code></span>
                  <span className="list__meta">{p.is_regex ? 'regex' : 'glob'}</span>
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>

      {(item.cta_text || item.cta_url) && (
        <section className="card" style={{ marginTop: 14 }}>
          <h3 className="card__title">CTA</h3>
          <dl style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '8px 14px', fontSize: 13, margin: '8px 0 0' }}>
            {item.cta_text && (<><dt style={{ color: 'var(--text-3)' }}>Label</dt><dd style={{ margin: 0 }}>{item.cta_text}</dd></>)}
            {item.cta_url && (<><dt style={{ color: 'var(--text-3)' }}>Target URL</dt><dd style={{ margin: 0 }}><code>{item.cta_url}</code></dd></>)}
          </dl>
        </section>
      )}
    </div>
  );
}

function statusCls(status) {
  const s = (status || '').toLowerCase();
  if (s === 'published') return 'badge--editor';
  if (s === 'draft') return 'badge--admin';
  if (s === 'archived') return 'badge--default';
  return 'badge--viewer';
}

/** Lifecycle buttons that mirror the tour StatusActions pattern.
 *  Content has the same draft/published/archived states + the
 *  same /publish + /archive routes server-side. */
function ContentLifecycle({ item, api, onChanged }) {
  const [busy, setBusy] = useState(null);
  const [confirmNode, ask] = useConfirm();
  const status = (item.status || '').toLowerCase();

  async function call(label, fn) {
    setBusy(label);
    try {
      const next = await fn();
      onChanged(next);
    } finally { setBusy(null); }
  }

  return (
    <div style={{ display: 'inline-flex', gap: 6 }}>
      {status === 'draft' && (
        <button type="button" className="btn btn--primary" disabled={busy !== null}
          onClick={() => call('publish', () => api(`/v1/admin/content/${encodeURIComponent(item.content_id)}/publish`, { method: 'POST' }))}>
          <CheckIcon /> {busy === 'publish' ? 'Publishing…' : 'Publish'}
        </button>
      )}
      {status === 'published' && (
        <button type="button" className="btn btn--ghost" disabled={busy !== null}
          onClick={() => call('unpublish', () => api(`/v1/admin/content/${encodeURIComponent(item.content_id)}`, { method: 'PUT', body: JSON.stringify({ status: 'draft' }) }))}>
          <EyeOffIcon /> {busy === 'unpublish' ? 'Unpublishing…' : 'Unpublish'}
        </button>
      )}
      {status === 'archived' ? (
        <button type="button" className="btn btn--ghost" disabled={busy !== null}
          onClick={() => call('restore', () => api(`/v1/admin/content/${encodeURIComponent(item.content_id)}`, { method: 'PUT', body: JSON.stringify({ status: 'draft' }) }))}>
          <RefreshIcon /> {busy === 'restore' ? 'Restoring…' : 'Restore'}
        </button>
      ) : (
        <button type="button" className="btn btn--danger-ghost" disabled={busy !== null}
          onClick={() => ask({
            title: `Archive "${item.title}"?`,
            body: 'Users stop seeing it; you can restore later.',
            confirmLabel: 'Archive',
            danger: true,
            runOnConfirm: () => call('archive', () => api(`/v1/admin/content/${encodeURIComponent(item.content_id)}/archive`, { method: 'POST' })),
          })}>
          <DeleteIcon />
          {busy === 'archive' ? 'Archiving…' : 'Archive'}
        </button>
      )}
      {confirmNode}
    </div>
  );
}
